import { Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { VDialogRef } from '@app/directives/_layout/v-dialog/v-dialog-ref';
import { DialogService } from '@app/directives/_layout/v-dialog/v-dialog.service';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { IFilterBarTab } from '@app/directives/_resourceFilters/filter-bar/filter-bar.models';
import { FilterBarService } from '@app/directives/_resourceFilters/filter-bar/filter-bar.service';
import { SortDirection } from '@app/enum/SortDirection';
import { SortType } from '@app/enum/SortType';
import { IVehicle } from '@app/interfaces/IVehicle';
import { EngineerJobItemStatus, EngineerJobItemType } from '@app/main/modules/foundation/foundation.enums';
import { ICsvImportCompletionResultDTO, IEngineerCommission, IEngineerJob, IEngineerJobItem, IJobItemValidationResultDTO } from '@app/main/modules/foundation/foundation.interfaces';
import { EngineerCommissionService } from '@app/main/modules/foundation/_services/engineer-commission.service';
import { EngineerJobItemService } from '@app/main/modules/foundation/_services/engineer-job-item.service';
import { FoundationVehiclesService } from '@app/main/modules/foundation/_services/vehicle.service';
import { TranslationService } from '@app/services/translation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { JobDetailsComponent } from '../job-details/job-details.component';
import './job-item-list.component.scss';

@UntilDestroy()
@Component({
    selector: 'job-item-list',
    template: require('./job-item-list.component.html')
})
export class JobItemListComponent implements OnInit {
    @Input() job: IEngineerJob;
    @Input() accountId: number;

    @Output() lastJobItemCancelled = new EventEmitter<void>();
    @ViewChild('lastJobItemConfirmation') jobCloseConfirm: TemplateRef<any>;
    //--

    jobItems: IEngineerJobItem[] = [];
    allOpenJobItems: IEngineerJobItem[] = [];
    selectedJobItem: IEngineerJobItem;

    commissions: IEngineerCommission[];

    filterTabs: IFilterBarTab<IEngineerJobItem>[] = [];

    vehicles: IVehicle[];
    vehiclesMap: Map<number, IVehicle>;

    duplicateJobId: number;

    loading: boolean;

    deleteConfirmSubjectTranslation = this.translationService.t('EngineerJobs.JobItem');

    EngineerJobItemType = EngineerJobItemType;
    EngineerJobItemStatus = EngineerJobItemStatus;

    tableColumns: IVTableColumn<IEngineerJobItem>[];
    tableRows: IVTableRow[];

    showIssueReporter: boolean;

    messageDialog: VDialogRef;


    constructor(
        @Inject(forwardRef(() => JobDetailsComponent)) private parent: JobDetailsComponent,
        private readonly dialogService: DialogService,
        private readonly engineerCommissionService: EngineerCommissionService,
        private readonly engineerJobItemService: EngineerJobItemService,
        private readonly filterBarService: FilterBarService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: FoundationVehiclesService,
        private readonly vTableService: VTableService
    ) { }

    ngOnInit(): void {
        this.loadData(true);
        this.createTable();
        this.createTabs();
        this.parent.newJobItem.pipe(untilDestroyed(this)).subscribe(value => {

            this.selectedJobItem = {
                JobId: this.job.Id,
                Status: EngineerJobItemStatus.Incomplete,
                Comments: ""
            };
        })
    }


    selectJobItem(row: IVTableRow): void {
        this.selectedJobItem = row.data;
    }

    clearSelectedJobItem(): void {
        this.selectedJobItem = null;
    }

    onLastJobItemCancelled(): void {
        this.messageDialog = this.dialogService.open(this.jobCloseConfirm, undefined);
    }

    onNewVehicleAdded(vehicle: IVehicle): void {
        this.vehicles.push(vehicle);
        this.vehiclesMap.set(vehicle.Id, vehicle);
    }

    cancelRemove(): void {
        this.messageDialog?.close();
    }

    confirmRemove(): void {
        this.messageDialog?.close();
        this.selectedJobItem = null;
        this.lastJobItemCancelled.emit();
    }

    vehicleFilterPredicate(item: any, value?: IVehicle): boolean {
        // Try to find vehicle id
        if (item.VehicleId) {
            return item.VehicleId == value.Id;
        }

        if (item.vehicle) {
            return item.vehicle.Id == value.Id;
        }

        if (item.ShortReg) {
            return item.Id == value.Id;
        }

        // Dont know how to cope with this
        return false;
    }

    vehicleSearchPredicate = (vehicle: IVehicle, value?: any): boolean => {
        return vehicle.ShortReg.toLowerCase().indexOf(value) >= 0 || vehicle.Reg.toLowerCase().indexOf(value) >= 0;
    }

    checkForDuplicateJobItems(vehicle: IVehicle): void {
        if (!vehicle) {
            this.duplicateJobId = null;
            return;
        }
        const duplicate = this.allOpenJobItems.find(jobItem => jobItem.VehicleId === vehicle.Id);
        if (duplicate) {
            this.duplicateJobId = duplicate.JobId;
            return;
        }
        this.duplicateJobId = null;
    }

    onCsvModelsSaved(event: ICsvImportCompletionResultDTO): void {
        this.loading = true;
        const createdModels = JSON.parse(event.CreatedModels) as IJobItemValidationResultDTO[];

        if (!this.jobItems) {
            this.jobItems = [];
        }

        createdModels.forEach(created => {
            this.jobItems.push(created.EngineerJobItem);
            this.vehicles.push(created.Vehicle)
        })

        this.vehiclesMap = this.vehicleService.createVehicleMap(this.vehicles);
        this.mapVehiclesAndCommissionsToJobItems();

        this.loading = false;
    }

    onReportIssueClicked(): void {
        if (!this.selectedJobItem) {
            return;
        }

        this.openIssueReportSidebar();
    }

    onCloseIssueReporter(): void {
        this.closeIssueReportSidebar();
    }

    //--

    private loadData(loading = false): void {
        if (!this.job?.Id) {
            return;
        }
        this.loading = loading;


        combineLatest([
            this.engineerJobItemService.getAllEngineerJobItems(),
            this.vehicleService.getVehiclesForAccount(this.accountId),
            this.engineerCommissionService.getEngineerCommissions({ JobId: this.job.Id })
        ]).pipe(untilDestroyed(this))
            .subscribe(([jobItems, vehicles, commissions]) => {
                this.jobItems = jobItems.filter(jobItem => jobItem.JobId === this.job.Id);
                this.allOpenJobItems = jobItems.filter(jobItem => jobItem.Status === EngineerJobItemStatus.Incomplete)
                this.vehicles = vehicles;

                this.vehiclesMap = this.vehicleService.createVehicleMap(vehicles);
                this.commissions = commissions;

                this.mapVehiclesAndCommissionsToJobItems();
                this.loading = false;
            });
    }
    
    private renderTable(): void {
        this.createTabs();
        this.tableRows = this.vTableService.createTableRows(this.jobItems, this.tableColumns);
    }

    private mapVehiclesAndCommissionsToJobItems(): void {
        if (!this.vehiclesMap || !this.jobItems) {
            return;
        }

        this.jobItems.forEach(jobItem => {
            jobItem.vehicle = this.vehiclesMap.get(jobItem.VehicleId);
            jobItem.commission = this.commissions.find(commission => commission.JobItemId === jobItem.Id);
        })

        this.renderTable();
    }

    private openIssueReportSidebar(): void {
        this.showIssueReporter = true;
    }

    private closeIssueReportSidebar(): void {
        this.showIssueReporter = false;
        this.selectedJobItem = null;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: "ID", width: 100, sorted: true, sortType: SortType.Number, sortDirection: SortDirection.Descending }),
                createCellFunction: (jobItem) => this.vTableService.createTableCell(jobItem.Id)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (jobItem) => this.vTableService.createVehicleRegCell(jobItem.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerJobs.JobItemType" }),
                createCellFunction: (jobItem) => this.vTableService.createEnumCell(EngineerJobItemType, "EngineerJobItemType", jobItem.Type)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status" }),
                createCellFunction: (jobItem) => this.vTableService.createEnumCell(EngineerJobItemStatus, "EngineerJobItemStatus", jobItem.Status)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerJobs.Instructions" }),
                createCellFunction: (jobItem) => this.vTableService.createTableCell(jobItem.Comments)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "EngineerJobs.Commission" }),
                createCellFunction: (jobItem) => this.vTableService.createTableCell(jobItem.commission?.CommissionNumber == 0 ? jobItem.commission?.Id : jobItem.commission?.CommissionNumber)
            }
        ]
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerJobItem>("EngineerJobs.Active", (jobItem) => jobItem.Status === EngineerJobItemStatus.Incomplete));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IEngineerJobItem>("EngineerJobs.Closed", (jobItem) => jobItem.Status !== EngineerJobItemStatus.Incomplete));
    }
}
